import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  FileText,
  BookOpen,
  FolderTree,
  Clock,
  ShoppingBag,
  Package,
  MessageSquare,
  Grid
} from 'lucide-react';
import adminService from '../../api/services/adminService';

const StatCard = ({ title, value, icon: Icon, color, link }) => (
  <Link
    to={link}
    className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow border border-gray-100"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getDashboardStats();
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome to the NLS Admin Panel</p>
      </div>

      {/* Stats Grid — Content */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Content</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title="Total Users" value={stats?.counts?.users || 0} icon={Users} color="bg-blue-500" link="/admin/users" />
          <StatCard title="Blog Posts" value={stats?.counts?.blogs || 0} icon={FileText} color="bg-green-500" link="/admin/blogs" />
          <StatCard title="Books" value={stats?.counts?.books || 0} icon={BookOpen} color="bg-purple-500" link="/admin/books" />
          <StatCard title="Categories" value={stats?.counts?.categories || 0} icon={FolderTree} color="bg-orange-500" link="/admin/categories" />
        </div>
      </div>

      {/* Stats Grid — Store */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Store</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title="Orders" value={stats?.counts?.orders || 0} icon={ShoppingBag} color="bg-rose-500" link="/admin/orders" />
          <StatCard title="Bundles" value={stats?.counts?.bundles || 0} icon={Package} color="bg-indigo-500" link="/admin/bundles" />
          <StatCard title="Quote Requests" value={stats?.counts?.quotes || 0} icon={MessageSquare} color="bg-amber-500" link="/admin/quotes" />
          <StatCard title="Store Categories" value={stats?.counts?.storeCategories || 0} icon={Grid} color="bg-teal-500" link="/admin/store-categories" />
        </div>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Users by Role */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-500" />
            Users by Role
          </h3>
          <div className="space-y-3">
            {Object.entries(stats?.analytics?.usersByRole || {}).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{role}</span>
                <span className="font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded-full text-sm">
                  {count}
                </span>
              </div>
            ))}
            {Object.keys(stats?.analytics?.usersByRole || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No user data available</p>
            )}
          </div>
        </div>

        {/* Blogs by Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-green-500" />
            Blogs by Status
          </h3>
          <div className="space-y-3">
            {Object.entries(stats?.analytics?.blogsByStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{status}</span>
                <span
                  className={`font-semibold px-3 py-1 rounded-full text-sm ${
                    status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {count}
                </span>
              </div>
            ))}
            {Object.keys(stats?.analytics?.blogsByStatus || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No blog data available</p>
            )}
          </div>
        </div>

        {/* Books by Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-purple-500" />
            Books by Status
          </h3>
          <div className="space-y-3">
            {Object.entries(stats?.analytics?.booksByStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{status.replace('_', ' ')}</span>
                <span className="font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded-full text-sm">
                  {count}
                </span>
              </div>
            ))}
            {Object.keys(stats?.analytics?.booksByStatus || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No book data available</p>
            )}
          </div>
        </div>

        {/* Orders by Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ShoppingBag className="w-5 h-5 mr-2 text-rose-500" />
            Orders by Status
          </h3>
          <div className="space-y-3">
            {Object.entries(stats?.analytics?.ordersByStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{status.replace(/_/g, ' ')}</span>
                <span className="font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded-full text-sm">
                  {count}
                </span>
              </div>
            ))}
            {Object.keys(stats?.analytics?.ordersByStatus || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No order data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-blue-500" />
              Recent Users
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recentUsers?.length > 0 ? (
              stats.recentUsers.map((user) => (
                <div key={user._id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      user.role === 'superadmin'
                        ? 'bg-red-100 text-red-700'
                        : user.role === 'admin'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {user.role}
                  </span>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-400">No recent users</div>
            )}
          </div>
        </div>

        {/* Recent Blogs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-green-500" />
              Recent Blog Posts
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recentBlogs?.length > 0 ? (
              stats.recentBlogs.map((blog) => (
                <div key={blog._id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{blog.title}</p>
                      <p className="text-sm text-gray-500">
                        by {blog.author?.name || 'Unknown'}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ml-2 ${
                        blog.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {blog.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-400">No recent blog posts</div>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ShoppingBag className="w-5 h-5 mr-2 text-rose-500" />
              Recent Orders
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recentOrders?.length > 0 ? (
              stats.recentOrders.map((order) => (
                <div key={order._id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 font-mono text-sm">
                        #{order.orderNumber || order._id?.slice(-8).toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-500">{order.user?.name || order.user?.email || '—'}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {order.currency} {order.total?.toFixed(2)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-400">No recent orders</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
